/**
 * WarrantLeverageComparison — Round P140·7 (Feb 2026)
 *
 * Compact "Compare all my warrants" strip rendered inside
 * WarrantExposureCard. For each warrant the user holds, shows the 90d
 * warrant %, underlying %, leverage ratio, and a classification pill
 * (Outperforming / In-line / Lagging / No data).
 *
 * Silent when the user holds zero warrants (parent already gates on
 * has_warrants). Data-quality-aware: a row where Massive lacked
 * enough shared history renders "—" for pct fields.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useActivePortfolioId } from '@/components/PortfolioSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WarrantLeverageComparison() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { activeId: activePortfolioId } = useActivePortfolioId();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { days: 90 };
    if (activePortfolioId) params.portfolio_id = activePortfolioId;
    axios
      .get(`${API}/portfolio/warrants-leverage-summary`, { params, withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activePortfolioId]);

  if (loading) return (
    <p className="text-[10px] text-muted-foreground italic pt-2" data-testid="warrant-leverage-loading">
      Loading 90d leverage comparison…
    </p>
  );
  if (!data?.has_warrants || !data.holdings?.length) return null;

  const fmtPct = (n) => n == null ? '—' : `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
  const clsClasses = (c) => ({
    outperforming: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 ring-emerald-500/50',
    in_line:       'bg-slate-500/20 text-slate-700 dark:text-slate-300 ring-slate-500/40',
    lagging:       'bg-rose-500/20 text-rose-700 dark:text-rose-300 ring-rose-500/50',
    no_data:       'bg-muted text-muted-foreground ring-border/50',
  }[c] || 'bg-muted text-muted-foreground ring-border/50');
  const clsLabel = (c) => ({
    outperforming: 'Outperforming',
    in_line:       'In line',
    lagging:       'Lagging',
    no_data:       'No data',
  }[c] || c);

  return (
    <div className="mt-3 pt-2 border-t border-amber-500/25" data-testid="warrant-leverage-comparison">
      <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1">
        90d Leverage vs Underlying
      </div>
      <table className="w-full text-[11px]">
        <thead className="text-[9px] uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="text-left py-0.5 font-semibold">Ticker</th>
            <th className="text-left py-0.5 font-semibold">Underlying</th>
            <th className="text-right py-0.5 font-semibold">Warrant Δ</th>
            <th className="text-right py-0.5 font-semibold">Underlying Δ</th>
            <th className="text-right py-0.5 font-semibold">Leverage</th>
            <th className="text-center py-0.5 font-semibold">Result</th>
          </tr>
        </thead>
        <tbody>
          {data.holdings.map((h) => {
            const wPos = (h.warrant_pct || 0) >= 0;
            const uPos = (h.underlying_pct || 0) >= 0;
            return (
              <tr key={h.ticker} className="border-t border-amber-500/15" data-testid={`warrant-lev-row-${h.ticker}`}>
                <td className="py-1 font-mono font-bold">{h.ticker}</td>
                <td className="py-1 font-mono">{h.underlying_ticker || '—'}</td>
                <td className={`py-1 text-right font-mono ${h.warrant_pct == null ? 'text-muted-foreground' : wPos ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                  {fmtPct(h.warrant_pct)}
                </td>
                <td className={`py-1 text-right font-mono ${h.underlying_pct == null ? 'text-muted-foreground' : uPos ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                  {fmtPct(h.underlying_pct)}
                </td>
                <td className="py-1 text-right font-mono font-bold">
                  {h.leverage_ratio == null ? '—' : `${h.leverage_ratio.toFixed(2)}×`}
                </td>
                <td className="py-1 text-center">
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ring-1 ${clsClasses(h.classification)}`}
                    data-testid={`warrant-lev-cls-${h.ticker}`}
                  >
                    {clsLabel(h.classification)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
